import {
  DetailType,
  SoccerGoalAttribute,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'

import {
  AttributeConfig,
  DetailConfiguration,
  getAttributeId,
  OnSelectOptions,
} from '../../../../composables/useSportOptions'
import { hasIncidentSomeAttribute } from '../../../../Modals/utils'
import {
  onSelectTechniqueOfShot,
  onSelectTechniqueOfShotDetails,
} from '../../utils'
import { techniqueOfShotDetailsAttributes } from './techniqueOfShotDetailsAttributes'

export enum GroupName {
  PatternOfPlay = 'Pattern of play',
  Distance = 'Distance',
  Placement = 'Placement',
  ShotWoodwork = 'Shot woodwork',
  TechniqueOfShot = 'Technique of shot',
  TechniqueOfShotDetails = 'Technique of shot - details',
  PressureOfDefenders = 'Pressure of defenders',
  NumberOfDefendersOnTheLineOfShot = 'Number of defenders on the line of shot',
  AdditionalAttributes = 'Additional attributes',
  Goalkeeper = 'Goalkeeper',
}

export const groups: Map<
  GroupName,
  DetailConfiguration<SoccerGoalAttribute>
> = new Map([
  [
    GroupName.Distance,
    {
      type: DetailType.Button,
      name: GroupName.Distance,
      important: true,
      attributes: [
        SoccerGoalAttribute.InsidePenaltyArea,
        SoccerGoalAttribute.OutsidePenaltyArea,
      ],
      cols: 2,
      multiple: false,
    },
  ],
  [
    GroupName.PatternOfPlay,
    {
      type: DetailType.Button,
      name: GroupName.PatternOfPlay,
      important: true,
      attributes: [
        SoccerGoalAttribute.OpenPlay,
        SoccerGoalAttribute.FastBreak,
        SoccerGoalAttribute.SetPiece,
      ],
      cols: 3,
    },
  ],
  [
    GroupName.Placement,
    {
      type: DetailType.Button,
      name: GroupName.Placement,
      important: true,
      attributes: [
        SoccerGoalAttribute.UpperLeft,
        SoccerGoalAttribute.UpperMiddle,
        SoccerGoalAttribute.UpperRight,
        SoccerGoalAttribute.BottomLeft,
        SoccerGoalAttribute.BottomMiddle,
        SoccerGoalAttribute.BottomRight,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.ShotWoodwork,
    {
      type: DetailType.Button,
      important: false,
      name: GroupName.ShotWoodwork,
      attributes: [SoccerGoalAttribute.Crossbar, SoccerGoalAttribute.Post],
      cols: 2,
      multiple: false,
    },
  ],
  [
    GroupName.TechniqueOfShot,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShot,
      important: true,
      attributes: [
        SoccerGoalAttribute.Normal,
        {
          attributeId: SoccerGoalAttribute.FirstTouch,
          isActive: (attributes: SoccerGoalAttribute[]) => {
            const validAttributes = [
              SoccerGoalAttribute.Normal,
              SoccerGoalAttribute.Volley,
            ]
            return attributes.some((attribute) =>
              validAttributes.includes(attribute),
            )
          },
        } as AttributeConfig<SoccerGoalAttribute>,
        SoccerGoalAttribute.Volley,
        SoccerGoalAttribute.Head,
        SoccerGoalAttribute.DirectFreeKick,
        SoccerGoalAttribute.BicycleKick,
        SoccerGoalAttribute.OtherBodyPart,
      ],
      cols: 3,
      multiple: false,
      onSelect: (opts: OnSelectOptions<SoccerGoalAttribute>) =>
        onSelectTechniqueOfShot(opts, SoccerGoalAttribute.FirstTouch),
    },
  ],
  [
    GroupName.TechniqueOfShotDetails,
    {
      type: DetailType.Button,
      name: GroupName.TechniqueOfShotDetails,
      important: true,
      attributes: techniqueOfShotDetailsAttributes,
      cols: 2,
      multiple: false,
      fulfilledWhen: (
        _attributes: SoccerGoalAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        const isFulfilled = probableIncident.attribute_ids.some(
          (attribute) =>
            attribute === SoccerGoalAttribute.Head ||
            attribute === SoccerGoalAttribute.OtherBodyPart,
        )

        if (isFulfilled) return true

        return hasIncidentSomeAttribute(
          probableIncident,
          techniqueOfShotDetailsAttributes.map((attribute) =>
            getAttributeId(attribute),
          ),
        )
      },
      onSelect: (opts: OnSelectOptions<SoccerGoalAttribute>) =>
        onSelectTechniqueOfShotDetails(opts, [
          [SoccerGoalAttribute.RightFoot, SoccerGoalAttribute.LeftFoot],
          [SoccerGoalAttribute.LeftFoot, SoccerGoalAttribute.RightFoot],
          [SoccerGoalAttribute.PowerShot, SoccerGoalAttribute.Chip],
          [SoccerGoalAttribute.Chip, SoccerGoalAttribute.PowerShot],
        ]),
    },
  ],
  [
    GroupName.PressureOfDefenders,
    {
      type: DetailType.Button,
      name: GroupName.PressureOfDefenders,
      important: true,
      attributes: [
        SoccerGoalAttribute.NoPressure,
        SoccerGoalAttribute.LightPressure,
        SoccerGoalAttribute.HighPressure,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.NumberOfDefendersOnTheLineOfShot,

    {
      type: DetailType.Button,
      name: GroupName.NumberOfDefendersOnTheLineOfShot,
      important: true,
      attributes: [
        SoccerGoalAttribute.ClearPosition,
        SoccerGoalAttribute.OneDefender,
        SoccerGoalAttribute.TwoOrMoreDefenders,
      ],
      cols: 3,
      multiple: false,
    },
  ],
  [
    GroupName.AdditionalAttributes,

    {
      type: DetailType.Button,
      name: GroupName.AdditionalAttributes,
      important: false,
      attributes: [
        SoccerGoalAttribute.BigChanceSuccessful,
        SoccerGoalAttribute.OneOnOne,
        SoccerGoalAttribute.UncomfortablePosition,
        SoccerGoalAttribute.EmptyNet,
        SoccerGoalAttribute.GoalkeeperInvolvement,
      ],
      cols: 4,
      multiple: true,
    },
  ],
  [
    GroupName.Goalkeeper,

    {
      type: DetailType.Select,
      name: GroupName.Goalkeeper,
      important: true,
      attributes: [],
      optionsFactory: () => [],
      fulfilledWhen: (
        _: SoccerGoalAttribute[],
        probableIncident?: ProbableIncident,
      ) => {
        if (!probableIncident) return false

        return !!probableIncident.additional_info?.goalkeeper_id
      },
    },
  ],
])
